import axios, { AxiosInstance } from 'axios';
import {
  User,
  Competence,
  Position,
  VerifyResult,
  Specialization,
  Company,
  Vacancy,
  UserPosition
} from '../types/api';
import { Interview, InterviewResult } from 'types/interview';
import { JobState } from 'types/job';
import { omit } from 'lodash';
import { ExctractResults } from 'types/skills';
import { NewCompany } from 'stores/companyStore';
import { RequestedInterview } from 'types/vacancy';
import { VacancyInterviewStats } from 'types/vacancyStats';
import { normalizePhone } from 'utils/phone';

// Используем переменные окружения из .env файла
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
export const API_AUTH_URL = process.env.REACT_APP_API_AUTH_URL || '';

const DEFAULT_TIMEOUT = 5 * 60 * 10000; // 5 минут

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

const authApi = axios.create({
  baseURL: API_AUTH_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Отдельный клиент без интерцепторов для refresh-запроса, чтобы не попасть в цикл
const refreshClient = axios.create({
  baseURL: API_AUTH_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Интерцептор для добавления токена авторизации
const attachAuthHeader = (instance: AxiosInstance) =>
  instance.interceptors.request.use(
    (config: any) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );

attachAuthHeader(authApi);
attachAuthHeader(api);

/** Сброс токенов только при ошибках авторизации (401/403). Не сбрасывать при network/SSL ошибках. */
export const isAuthError = (err: any): boolean => {
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

// Интерцептор для обработки ошибок авторизации (для обоих инстансов)
const attach401Handler = (instance: AxiosInstance) =>
  instance.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest: any = error?.config || {};

      // Если 401 пришёл на самом refresh-запросе — выходим из цикла и вылогиниваем
      const requestUrl: string = originalRequest?.url || '';
      if (error?.response?.status === 401 && requestUrl?.includes('/auth/refresh')) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');
        window.location.href = '/';
        return Promise.reject(error);
      }

      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            const response = await userApi.refreshToken(refreshToken);
            localStorage.setItem('accessToken', response.accessToken);

            // Повторяем оригинальный запрос с новым токеном
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
            // Используем исходную конфигурацию, чтобы сохранить исходный baseURL и инстанс
            return axios(originalRequest);
          }
        } catch (refreshError) {
          // Сбрасываем токены только при 401/403. При network/SSL ошибках (ERR_SSL_PROTOCOL_ERROR и т.п.) — сохраняем токены
          if (isAuthError(refreshError)) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('userId');
            window.location.href = '/';
          }
        }
      }

      return Promise.reject(error);
    }
  );

attach401Handler(authApi);
attach401Handler(api);

// Ретраи для api и authApi: 2 попытки с задержками 2000мс и 10000мс
const RETRY_DELAYS_MS = [2000, 10000];

const shouldRetry = (error: any): boolean => {
  if (error?.response) {
    const status = error.response.status;
    // Ретраим только серверные ошибки, не клиентские
    return status >= 500 && status < 600;
  }
  // Сетевые ошибки и таймауты — ретраим
  return true;
};

// Интерцептор ретраев (для обоих инстансов). Повтор идёт через тот же instance, чтобы сохранить baseURL
const attachRetryHandler = (instance: AxiosInstance) =>
  instance.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest: any = error?.config || {};

      if (!shouldRetry(error)) {
        return Promise.reject(error);
      }

      originalRequest.__retryCount = originalRequest.__retryCount || 0;

      if (originalRequest.__retryCount >= RETRY_DELAYS_MS.length) {
        return Promise.reject(error);
      }

      const delay = RETRY_DELAYS_MS[originalRequest.__retryCount];
      originalRequest.__retryCount += 1;

      await new Promise((resolve) => setTimeout(resolve, delay));
      return instance(originalRequest);
    }
  );

attachRetryHandler(authApi);
attachRetryHandler(api);

export const roleApi = {
  createRoleRequest: async ({
    requestedRole
  }: {
    requestedRole: 'HR' | 'CANDIDATE';
  }): Promise<any> => {
    const { data } = await authApi.post(`/role-requests`, { requestedRole });
    return data;
  }
};
// User endpoints
export const userApi = {
  requestedInterviews: async (id: string): Promise<RequestedInterview[]> => {
    const { data } = await authApi.get<RequestedInterview[]>(`/users/${id}/requested-interviews`);
    return data;
  },

  setExtractInterview: async (id: string, payload: any): Promise<any> => {
    // language_code -  не забыть вернуть
    const { data } = await authApi.post(
      `interviews/users/${id}/extract`,
      omit(payload, 'language_code')
    );
    return data;
  },

  updateExtractInterview: async (
    id: string,
    payload: { results?: ExctractResults; index?: number; finish?: boolean; isAnswered?: boolean }
  ): Promise<any> => {
    const { data } = await authApi.patch(`/interviews/users/extract/${id}`, payload);
    return data;
  },

  getExtractInterviews: async (id: string): Promise<any> => {
    const { data } = await authApi.get<any>(`/users/${id}/extract-interview`);
    return data;
  },

  uploadFile: async (file: File, userId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'avatar'); // тип медиа
    formData.append('ownerId', userId); // id пользователя

    const res = await authApi.post(`/media/upload`, formData);
    return res.data.url;
  },

  getFileById: async (userId: string) => {
    const res = await authApi.get(`/media/owner/${userId}`);
    return res.data;
  },

  getSpecializations: async (search: string): Promise<Specialization[]> => {
    const { data } = await authApi.get(`/specializations`, { params: { search } });
    return data;
  },

  searchPositionsBySpecialization: async (search: string, id: number): Promise<UserPosition[]> => {
    const { data } = await authApi.get(`/specializations/${id}/positions`, { params: { search } });
    return data;
  },

  searchPositions: async (search: string): Promise<UserPosition[]> => {
    const { data } = await authApi.get(`/specializations/positions`, { params: { search } });
    return data;
  },

  getSpecializationsByPositionName: async (name: string): Promise<Specialization[]> => {
    const { data } = await authApi.get(
      `/specializations/by-name/${encodeURIComponent(name)}/specializations`
    );
    return data;
  },

  // searchPositions: async (query: string): Promise<{ items: any[] }> => {
  //   const { data } = await authApi.get(`https://api.hh.ru/suggests/vacancy_positions?host=hh.ru&text=${query}`);
  //   return data;
  // },

  getInterviewList: async (id: string): Promise<Interview[]> => {
    const { data } = await authApi.get<Interview[]>(`interviews/users/${id}`);
    return data;
  },

  getPayments: async (): Promise<any[]> => {
    const { data } = await authApi.get(`/users/payments`);
    return data;
  },

  getDiscounts: async (id: string): Promise<any> => {
    const { data } = await authApi.get(`/payment/discount/${id}`);
    return data;
  },

  getHrInvitationsPrices: async (): Promise<any> => {
    const { data } = await authApi.get(`/payment/hr/invitations/prices`);
    return data;
  },

  getHrInvitationsBalance: async (): Promise<any> => {
    const { data } = await authApi.get(`/payment/hr/invitations/balance`);
    return data;
  },

  getPrice: async (): Promise<{ finalPrice: string; fullPrice: string }> => {
    const { data } = await authApi.get(`/payment/price`);
    return data;
  },

  // Применение промокода. Для интервью бэкенд сохраняет скидку (после 200 нужно
  // перезапросить getPrice), для HR-приглашений в ответе приходит поле invitations.
  applyPromoCode: async (params: {
    code: string;
  }): Promise<{ invitations?: number; priceRub?: number; balance?: number }> => {
    const { data } = await authApi.post(`/promo-code/activate`, params);
    return data;
  },

  // Ссылка на оплату формируется на бэкенде: там же считается сумма,
  // Receipt (54-ФЗ) и SignatureValue (Пароль#1 не покидает сервер).
  getRobokassaUrl: async (params: {
    formId: string;
    paymentType: 'interview' | 'hr_invitation';
    pack?: number;
  }): Promise<string> => {
    const { data } = await authApi.get(`/payment/robokassa/url`, { params });
    return data.url;
  },

  createFreePayment: async (params: { formId: string }): Promise<string> => {
    const { data } = await authApi.post(`/payment/zero`, params);
    return data.url;
  },

  addInterviewToUser: async (id: string, interview: Interview): Promise<User> => {
    const { data } = await authApi.post<User>(`/interviews/users/${id}`, {
      ...omit(interview, ['id']),
      interview_id: interview.id
    });
    return data;
  },

  updateUserInterview: async (interviewId: string, payload: Partial<Interview>): Promise<User> => {
    const { data } = await authApi.patch<User>(`/interviews/users/${interviewId}`, {
      ...omit(payload, ['id', 'created_at'])
    });
    return data;
  },

  getAuthConfig: async (): Promise<{ otpStrategy: 'email' | 'phone' }> => {
    const { data } = await authApi.get('/auth/config');
    return data;
  },

  sendOtp: async (identifier: string, strategy: 'email' | 'phone' = 'email'): Promise<any> => {
    const payload = strategy === 'phone' ? { phone: identifier } : { email: identifier };
    const { data } = await authApi.post('/auth/send-otp', payload);
    return data;
  },

  verifydOtp: async (
    identifier: string,
    code: string,
    strategy: 'email' | 'phone' = 'email'
  ): Promise<VerifyResult> => {
    const payload =
      strategy === 'phone' ? { phone: identifier, code } : { email: identifier, code };
    const { data } = await authApi.post<VerifyResult>('/auth/verify-otp', payload);
    return data;
  },

  login: async (
    email: string | undefined,
    phone: string | undefined,
    password: string
  ): Promise<VerifyResult> => {
    const { data } = await authApi.post<VerifyResult>('/auth/login', {
      email,
      phone: normalizePhone(phone),
      password
    });
    return data;
  },

  sendResetOtp: async (identifier: string, strategy: 'email' | 'phone' = 'email'): Promise<any> => {
    const payload = strategy === 'phone' ? { phone: identifier } : { email: identifier };
    const { data } = await authApi.post('/auth/reset-password/send-otp', payload);
    return data;
  },

  confirmResetPassword: async (params: {
    identifier: string;
    strategy: 'email' | 'phone';
    code: string;
    newPassword: string;
  }): Promise<any> => {
    const { identifier, strategy, code, newPassword } = params;
    const payload =
      strategy === 'phone'
        ? { phone: identifier, code, newPassword }
        : { email: identifier, code, newPassword };
    const { data } = await authApi.post('/auth/reset-password/confirm', payload);
    return data;
  },

  refreshToken: async (refreshToken: string): Promise<{ accessToken: string }> => {
    // Используем отдельный клиент без интерцепторов, чтобы исключить повторную обработку 401
    const { data } = await refreshClient.post<{ accessToken: string }>('/auth/refresh', {
      refreshToken
    });
    return data;
  },

  getUserById: async (id: string): Promise<User> => {
    const { data } = await authApi.get<User>(`/users/${id}`);
    return data;
  },

  updateUser: async (
    id: string,
    userData: Partial<User> & { password?: string }
  ): Promise<User> => {
    const { data } = await authApi.patch<User>(`/users/${id}`, omit(userData, ['isActive']));
    return data;
  },

  setCompetenceScore: async (payload: { name: string; score: number }, id: string) => {
    await authApi.post(`/users/${id}/competences/`, payload);
  },

  setResults: async (
    interviewId: string,
    payload: {
      interview_id: string;
    } & InterviewResult
  ): Promise<Interview> => {
    const { data } = await authApi.post(`/interviews/${interviewId}/result`, payload);
    return data;
  },

  /**
   * Запустить асинхронный расчёт оценок (новый поток).
   * Возвращает jobId — результат прилетит WS-событием `interview.scores.ready`.
   * Параллелен setResults; включается флагом REACT_APP_USE_ASYNC_SCORES.
   */
  startScoresJob: async (
    interviewId: string,
    selfScore: Record<string, number>
  ): Promise<{ jobId: string; status: string }> => {
    const { data } = await authApi.post<{ jobId: string; status: string }>(
      `/interviews/${interviewId}/scores`,
      selfScore
    );
    return data;
  },

  /**
   * Recovery-эндпоинт: получить текущее состояние асинхронной задачи.
   * Используется при F5/reconnect, чтобы не пропустить уже завершённую джобу.
   */
  getJob: async (jobId: string): Promise<JobState> => {
    const { data } = await authApi.get<JobState>(`/jobs/${jobId}`);
    return data;
  },

  /**
   * Запустить асинхронное извлечение компетенций (новый поток).
   * Возвращает jobId — результат прилетит WS-событием `extract.skills.ready`.
   *
   * @param extractInterviewId — id записи ExtractInterview в auth
   * @param coreInterviewId    — id, который core ожидает в URL positions/{id}/competences
   */
  startExtractSkillsJob: async (
    extractInterviewId: string,
    coreInterviewId: string
  ): Promise<{ jobId: string; status: string }> => {
    const { data } = await authApi.post<{ jobId: string; status: string }>(
      `/interviews/users/extract/${extractInterviewId}/skills`,
      { interview_id: coreInterviewId }
    );
    return data;
  },

  getCompetenceScore: async (id: string) => {
    const { data } = await authApi.get<{ name: string; score: number }[]>(
      `/users/${id}/competences/`
    );
    return data;
  }
};

export const vacancyApi = {
  getById: async (id: string): Promise<Vacancy> => {
    const { data } = await authApi.get<Vacancy>(`/vacancy/${id}`);
    return data;
  },

  extractEmails: async (file: File | Blob, vacancyId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await authApi.post(`/vacancy/${vacancyId}/extract-emails`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  getByCompany: async (companyId: string): Promise<Vacancy[]> => {
    const { data } = await authApi.get<Vacancy[]>(`/vacancy/?companyId=${companyId}`);
    return data;
  },

  create: async (dto: CreateVacancyDto): Promise<Vacancy> => {
    const { data } = await authApi.post<Vacancy>(`/vacancy`, dto);
    return data;
  },

  update: async (id: string, dto: UpdateVacancyDto): Promise<Vacancy> => {
    const { data } = await authApi.patch<Vacancy>(`/vacancy/${id}`, dto);
    return data;
  },

  /**
   * Сгенерировать (подобрать) навыки по описательным полям вакансии.
   * Не требует id вакансии — можно вызвать ещё до создания вакансии.
   * Ответ — массив названий навыков.
   */
  generateSkills: async (dto: GenerateVacancySkillsDto): Promise<string[]> => {
    const { data } = await authApi.post<{ skills: string[] }>(`/vacancy/skills`, dto);
    return data.skills;
  },

  /**
   * Сохранить навыки в вакансию. Тело — объект { "<навык>": число|null } напрямую.
   * Поле skills перезаписывается целиком (не мержится). Доступно только владельцу.
   */
  updateSkills: async (id: string, skills: VacancySkills): Promise<Vacancy> => {
    const { data } = await authApi.patch<Vacancy>(`/vacancy/${id}/skills`, skills);
    return data;
  },

  addCandidates: async (vacancyId: string, emails: string[]): Promise<void> => {
    await authApi.post(
      `/vacancy/${vacancyId}/candidates`,
      { emails },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
  },

  getCandidates: async (vacancyId: string): Promise<any[]> => {
    const { data } = await authApi.get<any[]>(`/vacancy/${vacancyId}/candidates`);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await authApi.delete(`/vacancy/${id}`);
  },

  addToFavorites: async (vacancyId: string, candidateId: string): Promise<any> => {
    const { data } = await authApi.post(`/vacancy/${vacancyId}/candidates/${candidateId}/favorite`);
    return data;
  },

  removeFromFavorites: async (vacancyId: string, candidateId: string): Promise<any> => {
    const { data } = await authApi.delete(
      `/vacancy/${vacancyId}/candidates/${candidateId}/favorite`
    );
    return data;
  },

  resendInvitation: async (vacancyId: string, email: string): Promise<{ message: string }> => {
    const { data } = await authApi.post<{ message: string }>(
      `/vacancy/${vacancyId}/candidates/${encodeURIComponent(email)}/resend`
    );
    return data;
  },

  fetchInviteLink: async (
    vacancyId: string,
    email: string
  ): Promise<{ inviteLink: string; email: string }> => {
    const { data } = await authApi.post(
      `/vacancy/${vacancyId}/candidates/${encodeURIComponent(email)}/invite-link`
    );
    return data;
  }
};

export const companyApi = {
  createCompany: async (company: NewCompany): Promise<Company> => {
    const { data } = await authApi.post<Company>(`/company`, company);
    return data;
  },

  findAll: async (): Promise<Company[]> => {
    const { data } = await authApi.get<Company[]>(`/company`);
    return data;
  },

  findOne: async (id: string): Promise<Company> => {
    const { data } = await authApi.get<Company>(`/company/${id}`);
    return data;
  },

  update: async (
    id: string,
    dto: { name?: string; email?: string; inn?: string }
  ): Promise<Company> => {
    const { data } = await authApi.patch<Company>(`/company/${id}`, dto);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await authApi.delete(`/company/${id}`);
  },

  uploadLogo: async (id: string, file: File): Promise<Company> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await authApi.post<Company>(`/company/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  }
};

export type CreateVacancyDto = {
  companyId: string;
  speciality: string;
  specialityLevel: 'JUNIOR' | 'MIDDLE' | 'SENIOR';
  position: string;
};

export type UpdateVacancyDto = Partial<Omit<CreateVacancyDto, 'companyId'>>;

export type GenerateVacancySkillsDto = {
  position: string;
  speciality: string;
  level: 'JUNIOR' | 'MIDDLE' | 'SENIOR';
  description: string;
};

/**
 * Навыки вакансии: ключ — название навыка, значение — минимальный порог оценки
 * (число 1–10) либо null («порог не задан»). Содержимое не валидируется бэком.
 */
export type VacancySkills = Record<string, number | null>;

export const skillsApi = {
  extractCompetences: async (position: Position) => {
    const { data } = await api.post(`interview/extractCompetences`, {
      position: position.position,
      speciality: position.speciality,
      level: position.level?.toLowerCase()
    });
    return data;
  },
  sendVideo: async (id: string, videoAnswer: File | Blob) => {
    const formData = new FormData();
    formData.append('file', videoAnswer, 'recording.mp4');
    formData.append('chunk_index', String(0));

    // Изменяем заголовок для этого конкретного запроса
    const { data } = await api.post<Interview>(`/competences/${id}/video`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 2 * 60 * 1000 // 2 минуты
    });
    return data;
  },
  extractSkills: async (id: string) => {
    const { data } = await api.get<ExctractResults>(`positions/${id}/competences`);
    return data;
  }
};

// Chat endpoints
export type ChatConversation = {
  id: string;
  createdAt: string;
  updatedAt: string;
  participants: {
    id: string;
    userId: string;
    user: {
      id: string;
      firstName: string | null;
      lastName: string | null;
      avatar: string | null;
      email: string | null;
    };
  }[];
  messages: ChatMessage[];
};

export type ChatMessage = {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  type: 'TEXT' | 'MEETING_INVITE';
  metadata: Record<string, any> | null;
  isRead: boolean;
  createdAt: string;
  sender: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    avatar: string | null;
    email: string | null;
  };
};

export const chatApi = {
  findOrCreateConversation: async (recipientId: string): Promise<ChatConversation> => {
    const { data } = await authApi.post<ChatConversation>('/chat/conversations', { recipientId });
    return data;
  },

  findOrCreateConversationServiceUser: async (): Promise<ChatConversation> => {
    const { data } = await authApi.post<ChatConversation>('/chat/conversations/service');
    return data;
  },

  sendGuestSupportMessage: async (payload: {
    email: string;
    name: string;
    text: string;
  }): Promise<{ success: boolean }> => {
    const { data } = await authApi.post<{ success: boolean }>(
      '/chat/conversations/service/guest',
      payload
    );
    return data;
  },

  getConversations: async (): Promise<ChatConversation[]> => {
    const { data } = await authApi.get<ChatConversation[]>('/chat/conversations');
    return data;
  },

  getMessages: async (conversationId: string, skip = 0, take = 50): Promise<ChatMessage[]> => {
    const { data } = await authApi.get<ChatMessage[]>(
      `/chat/conversations/${conversationId}/messages`,
      {
        params: { skip, take }
      }
    );
    return data;
  },

  sendMessage: async (conversationId: string, text: string): Promise<ChatMessage> => {
    const { data } = await authApi.post<ChatMessage>(
      `/chat/conversations/${conversationId}/messages`,
      { text }
    );
    return data;
  },

  scheduleMeeting: async (
    conversationId: string,
    payload: { dates: string[]; times: string[]; durationHours: string; durationMinutes: string }
  ): Promise<ChatMessage> => {
    const { data } = await authApi.post<ChatMessage>(
      `/chat/conversations/${conversationId}/schedule-meeting`,
      payload
    );
    return data;
  },

  markAsRead: async (conversationId: string): Promise<void> => {
    await authApi.post(`/chat/conversations/${conversationId}/read`);
  },

  getUnreadCount: async (): Promise<{ count: number }> => {
    const { data } = await authApi.get<{ count: number }>('/chat/unread-count');
    return data;
  }
};

// Interview endpoints
export const interviewApi = {
  startInterview: async (id: string): Promise<Interview> => {
    const { data } = await api.post<Interview>('/interview/start', JSON.stringify({ id }));
    return data;
  },

  getInterviewsByVacancyId: async (vacancyId: string): Promise<Interview[]> => {
    const { data } = await authApi.get<Interview[]>(`/interviews/vacancies/${vacancyId}`);
    return data;
  },

  /**
   * Агрегированная аналитика по кандидатам вакансии (для HR-дашборда).
   * 403 — нет доступа/не авторизован, 404 — вакансия не найдена.
   * Навыки в ответе намеренно отсутствуют.
   */
  getVacancyStats: async (vacancyId: string): Promise<VacancyInterviewStats> => {
    const { data } = await authApi.get<VacancyInterviewStats>(
      `/interviews/vacancies/${vacancyId}/stats`
    );
    return data;
  },

  sendVideoChunk: async (
    id: string,
    videoChunk: File | Blob,
    index: number,
    options?: { timeout?: number }
  ): Promise<Interview> => {
    const formData = new FormData();

    formData.append('file', videoChunk);

    const { data } = await api.post<Interview>(
      `/interview/${id}/questions/chunk/${index}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: options?.timeout
      }
    );
    return data;
  },

  /**
   * Лёгкая проба соединения: запрашиваем метаданные интервью с коротким таймаутом.
   * Используется для pre-flight проверки перед стартом записи и для ре-проверки при ошибках.
   */
  probeInterviewConnection: async (id: string, timeoutMs = 10_000): Promise<void> => {
    await api.get<Interview>(`/interview/${id}`, { timeout: timeoutMs });
  },

  getNextQuestion: async (id: string): Promise<Interview> => {
    const { data } = await api.post<Interview>(`/interview/${id}/questions/next`);
    return data;
  },

  getInterviewResult: async (id: string): Promise<InterviewResult> => {
    const { data } = await api.get<InterviewResult>(`/interview/${id}/result`);
    return data;
  },

  getInterviewById: async (id: string): Promise<Interview> => {
    const { data } = await api.get<Interview>(`/interview/${id}`);
    return data;
  },

  getInterviewScores: async (
    id: string,
    selfScore: Record<string, number>
  ): Promise<InterviewResult> => {
    const { data } = await api.post<InterviewResult>(`/interview/${id}/show/scores`, {
      ...selfScore
    });
    return data;
  },

  /**
   * Публичный доступ к результатам интервью по расшаренной ссылке.
   * Не требует авторизации — используется на странице /shared/results/:id.
   */
  getSharedResultsById: async (id: string): Promise<Interview> => {
    const { data } = await authApi.get<Interview>(`/interviews/shared/interview/${id}`);
    return data;
  },

  createSharedLink: async (
    interviewId: string
  ): Promise<{ id: string; interviewId: string; userId: string }> => {
    const { data } = await authApi.post(`interviews/shared/interview/${interviewId}`);
    return data;
  }
};

export interface InvitationInfo {
  /** Email, на который выслано приглашение (показывается заблокированным) */
  email: string;
  position: string;
  company: string;
  /** true — у этого email ещё нет аккаунта, нужно задать пароль; false — есть, нужно войти */
  requiresPassword: boolean;
  expiresAt: string;
}

export interface AcceptInvitationResult {
  status: 'registered' | 'linked';
  accessToken?: string;
  refreshToken?: string;
}

export const invitationApi = {
  /** Данные приглашения по токену из ссылки — для экрана принятия (без авторизации) */
  getInvitation: async (token: string): Promise<InvitationInfo> => {
    const { data } = await authApi.get<InvitationInfo>(`/invitations/${token}`);
    return data;
  },

  /**
   * Принять приглашение.
   * - Гость: передаём password (+ имя) → бэк создаёт аккаунт и вернёт токены (status: 'registered').
   * - Авторизованный (есть accessToken в localStorage): payload пустой → бэк привяжет
   *   приглашение к текущему аккаунту (status: 'linked').
   */
  acceptInvitation: async (
    token: string,
    payload: { password?: string; firstName?: string; lastName?: string }
  ): Promise<AcceptInvitationResult> => {
    const { data } = await authApi.post<AcceptInvitationResult>(
      `/invitations/${token}/accept`,
      payload
    );
    return data;
  }
};
